import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../lib/LanguageContext';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, ChevronLeftIcon, ChevronRightIcon, GlobeIcon, PlaneIcon, BuildingIcon, MapPinIcon, HeartHandshakeIcon, BookOpenIcon, MailIcon } from 'lucide-react';
const TOTAL_SLIDES = 10;
export function PitchDeck() {
  const {
    t,
    tArray,
    language,
    setLanguage
  } = useLanguage();
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const goNext = useCallback(() => {
    setCurrentSlide(prev => Math.min(prev + 1, TOTAL_SLIDES - 1));
  }, []);
  const goPrev = useCallback(() => {
    setCurrentSlide(prev => Math.max(prev - 1, 0));
  }, []);
  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown' || e.key === ' ') {
        e.preventDefault();
        goNext();
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        goPrev();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goNext, goPrev]);
  // Mouse wheel navigation with debounce
  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | null = null;
    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      if (timeout) return;
      timeout = setTimeout(() => {
        timeout = null;
      }, 600);
      if (e.deltaY > 0 || e.deltaX > 0) goNext();else if (e.deltaY < 0 || e.deltaX < 0) goPrev();
    };
    window.addEventListener('wheel', handleWheel, {
      passive: false
    });
    return () => window.removeEventListener('wheel', handleWheel);
  }, [goNext, goPrev]);
  // Touch swipe navigation
  useEffect(() => {
    let startX = 0;
    let startY = 0;
    const handleTouchStart = (e: TouchEvent) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    };
    const handleTouchEnd = (e: TouchEvent) => {
      const dx = e.changedTouches[0].clientX - startX;
      const dy = e.changedTouches[0].clientY - startY;
      if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 50) {
        if (dx < 0) goNext();else goPrev();
      }
    };
    window.addEventListener('touchstart', handleTouchStart);
    window.addEventListener('touchend', handleTouchEnd);
    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [goNext, goPrev]);
  const handleDonateClick = () => {
    navigate('/');
    setTimeout(() => {
      document.getElementById('support')?.scrollIntoView({
        behavior: 'smooth'
      });
    }, 100);
  };
  const progress = currentSlide / (TOTAL_SLIDES - 1) * 100;
  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? '100%' : '-100%',
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      x: direction > 0 ? '-100%' : '100%',
      opacity: 0
    })
  };
  const [direction, setDirection] = useState(0);
  const goToSlide = useCallback((idx: number) => {
    setDirection(idx > currentSlide ? 1 : -1);
    setCurrentSlide(idx);
  }, [currentSlide]);
  useEffect(() => {
    setDirection(currentSlide > 0 ? 1 : 0);
  }, []);
  const handleNext = () => {
    setDirection(1);
    goNext();
  };
  const handlePrev = () => {
    setDirection(-1);
    goPrev();
  };
  // Override wheel/key to also set direction
  useEffect(() => {
    let timeout2: ReturnType<typeof setTimeout> | null = null;
    const handleWheel2 = (e: WheelEvent) => {
      if (e.deltaY > 0 || e.deltaX > 0) setDirection(1);else setDirection(-1);
    };
    window.addEventListener('wheel', handleWheel2, {
      passive: true
    });
    return () => window.removeEventListener('wheel', handleWheel2);
  }, []);
  useEffect(() => {
    const handleKey2 = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown' || e.key === ' ') setDirection(1);else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') setDirection(-1);
    };
    window.addEventListener('keydown', handleKey2);
    return () => window.removeEventListener('keydown', handleKey2);
  }, []);
  const slideClass = 'w-full h-full flex items-center justify-center px-8 md:px-16 py-20 relative overflow-hidden';
  const renderSlide = (idx: number) => {
    switch (idx) {
      case 0:
        return <div className={`${slideClass} bg-navy text-center`} data-id="element-286">
            <div className="absolute inset-0 z-0 bg-gradient-to-b from-navy/40 via-transparent to-navy/90" data-id="element-287" />
            <div className="absolute inset-0 z-0 opacity-20" style={{
            backgroundImage: 'url("https://cdn.magicpatterns.com/uploads/vUKHo6PP3utujbYnVGGx1Z/Gemini_Generated_Image_q0e33oq0e33oq0e3.png")',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }} data-id="element-288" />
            <div className="relative z-10 max-w-4xl mx-auto" data-id="element-289">
              <div className="w-16 h-[1px] bg-saffron mx-auto mb-6" data-id="element-290" />
              <h1 className="text-4xl md:text-6xl lg:text-7xl font-heading text-warmWhite mb-4 leading-tight" data-id="element-291">
                {t('pitchDeck.cover.title')}
              </h1>
              <p className="text-xl md:text-2xl text-saffron font-heading italic mb-6" data-id="element-292">
                {t('pitchDeck.cover.subtitle')}
              </p>
              <p className="text-base md:text-lg text-warmWhite/80 max-w-2xl mx-auto leading-relaxed text-balance" data-id="element-293">
                {t('pitchDeck.cover.description')}
              </p>
              <div className="w-16 h-[1px] bg-saffron mx-auto mt-8" data-id="element-294" />
            </div>
          </div>;
      case 1:
        return <div className={`${slideClass} bg-ivory text-navy`} data-id="element-295">
            <div className="max-w-5xl mx-auto w-full flex flex-col lg:flex-row items-center gap-8" data-id="element-296">
              <div className="flex-1 text-center lg:text-left" data-id="element-297">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-saffron/10 border border-saffron/30 mb-4" data-id="element-298">
                  <BookOpenIcon className="w-6 h-6 text-saffron" data-id="element-299" />
                </div>
                <h2 className="text-3xl md:text-5xl font-heading mb-3" data-id="element-300">
                  {t('pitchDeck.sacredNeed.title')}
                </h2>
                <p className="text-lg text-saffron font-heading italic mb-4" data-id="element-301">
                  {t('pitchDeck.sacredNeed.subtitle')}
                </p>
                <div className="w-12 h-[2px] bg-saffron mx-auto lg:mx-0 mb-4" data-id="element-302" />
                <p className="text-base md:text-lg text-navy/80 leading-relaxed" data-id="element-303">
                  {t('pitchDeck.sacredNeed.description')}
                </p>
              </div>
              <div className="flex-1 w-full relative flex items-center justify-center min-h-[250px]" data-id="element-304">
                <div className="aspect-square w-48 md:w-56 rounded-full border border-saffron/20 absolute animate-[spin_60s_linear_infinite]" data-id="element-305" />
                <div className="aspect-square w-56 md:w-64 rounded-full border border-navy/10 absolute animate-[spin_40s_linear_infinite_reverse]" data-id="element-306" />
                <div className="bg-navy p-8 rounded-full w-40 md:w-48 aspect-square flex items-center justify-center relative z-10 shadow-2xl" data-id="element-307">
                  <p className="text-warmWhite font-heading text-sm md:text-lg text-center italic leading-relaxed" data-id="element-308">
                    "Maharaj deserves a throne, not a temporary stage."
                  </p>
                </div>
              </div>
            </div>
          </div>;
      case 2:
        return <div className={`${slideClass} bg-navy text-warmWhite`} data-id="element-309">
            <div className="max-w-5xl mx-auto w-full" data-id="element-310">
              <div className="text-center mb-10" data-id="element-311">
                <h2 className="text-3xl md:text-5xl font-heading mb-4" data-id="element-312">
                  {t('pitchDeck.opportunity.title')}
                </h2>
                <div className="w-12 h-[2px] bg-saffron mx-auto mb-6" data-id="element-313" />
                <p className="text-base md:text-lg text-warmWhite/70 max-w-3xl mx-auto leading-relaxed text-balance" data-id="element-314">
                  {t('pitchDeck.opportunity.description')}
                </p>
              </div>
              <div className="grid grid-cols-3 gap-6" data-id="element-315">
                {tArray<{
                number: string;
                label: string;
              }>('pitchDeck.opportunity.stats').map((stat, idx) => <div key={idx} className="text-center p-6 border border-warmWhite/10 rounded-sm bg-warmWhite/5" data-id="element-316">
                    <div className="text-3xl md:text-5xl font-heading text-saffron mb-3" data-id="element-317">
                      {stat.number}
                    </div>
                    <div className="text-xs tracking-widest uppercase font-medium text-warmWhite/80" data-id="element-318">
                      {stat.label}
                    </div>
                  </div>)}
              </div>
            </div>
          </div>;
      case 3:
        return <div className={`${slideClass} bg-ivory text-navy`} data-id="element-319">
            <div className="max-w-5xl mx-auto w-full" data-id="element-320">
              <div className="text-center mb-10" data-id="element-321">
                <h2 className="text-3xl md:text-5xl font-heading mb-4" data-id="element-322">
                  {t('pitchDeck.whyCancun.title')}
                </h2>
                <div className="w-12 h-[2px] bg-saffron mx-auto" data-id="element-323" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-324">
                {tArray<{
                title: string;
                desc: string;
              }>('pitchDeck.whyCancun.points').map((point, idx) => {
                const icons = [MapPinIcon, PlaneIcon, BuildingIcon, GlobeIcon];
                const Icon = icons[idx % icons.length];
                return <div key={idx} className="flex items-start space-x-4" data-id="element-325">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-saffron/10 flex items-center justify-center border border-saffron/30" data-id="element-326">
                        <Icon className="w-5 h-5 text-saffron" data-id="element-327" />
                      </div>
                      <div data-id="element-328">
                        <h3 className="text-lg md:text-xl font-heading mb-2 text-saffron" data-id="element-329">
                          {point.title}
                        </h3>
                        <p className="text-navy/70 leading-relaxed text-sm md:text-base" data-id="element-330">
                          {point.desc}
                        </p>
                      </div>
                    </div>;
              })}
              </div>
            </div>
          </div>;
      case 4:
        return <div className={`${slideClass} bg-saffron text-navy text-center`} data-id="element-331">
            <div className="max-w-3xl mx-auto w-full" data-id="element-332">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-navy/10 mb-6" data-id="element-333">
                <HeartHandshakeIcon className="w-8 h-8 text-navy" data-id="element-334" />
              </div>
              <h2 className="text-3xl md:text-5xl font-heading mb-3" data-id="element-335">
                {t('pitchDeck.reality.title')}
              </h2>
              <p className="text-lg md:text-xl font-heading italic mb-6 text-navy/80" data-id="element-336">
                {t('pitchDeck.reality.subtitle')}
              </p>
              <div className="w-16 h-[2px] bg-navy/20 mx-auto mb-8" data-id="element-337" />
              <p className="text-lg md:text-xl leading-relaxed text-balance font-medium" data-id="element-338">
                {t('pitchDeck.reality.description')}
              </p>
            </div>
          </div>;
      case 5:
        return <div className={`${slideClass} bg-navy text-warmWhite`} data-id="element-339">
            <div className="max-w-5xl mx-auto w-full" data-id="element-340">
              <div className="text-center mb-10" data-id="element-341">
                <h2 className="text-3xl md:text-5xl font-heading mb-3" data-id="element-342">
                  {t('pitchDeck.vision.title')}
                </h2>
                <p className="text-lg text-saffron font-heading italic mb-4" data-id="element-343">
                  {t('pitchDeck.vision.subtitle')}
                </p>
                <div className="w-12 h-[2px] bg-saffron mx-auto" data-id="element-344" />
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-345">
                {tArray<{
                num: string;
                title: string;
                desc: string;
              }>('pitchDeck.vision.phases').map((phase, idx) => <div key={idx} className="p-6 border border-warmWhite/10 bg-warmWhite/5 relative overflow-hidden" data-id="element-346">
                    <div className="text-7xl font-heading text-warmWhite/5 absolute -top-4 -right-4" data-id="element-347">
                      {phase.num}
                    </div>
                    <div className="relative z-10" data-id="element-348">
                      <div className="text-xs tracking-widest uppercase text-saffron mb-3" data-id="element-349">
                        Phase {phase.num}
                      </div>
                      <h3 className="text-2xl font-heading mb-3" data-id="element-350">
                        {phase.title}
                      </h3>
                      <p className="text-warmWhite/70 leading-relaxed text-sm" data-id="element-351">
                        {phase.desc}
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>;
      case 6:
        return <div className={`${slideClass} bg-ivory text-navy`} data-id="element-352">
            <div className="max-w-3xl mx-auto w-full text-center" data-id="element-353">
              <div className="p-10 md:p-16 border border-saffron/20 bg-white shadow-xl rounded-sm relative" data-id="element-354">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-14 h-14 bg-navy border border-saffron/30 rounded-full flex items-center justify-center" data-id="element-355">
                  <BuildingIcon className="w-7 h-7 text-saffron" data-id="element-356" />
                </div>
                <h2 className="text-2xl md:text-4xl font-heading mb-6 mt-4" data-id="element-357">
                  {t('pitchDeck.ecosystem.title')}
                </h2>
                <div className="w-12 h-[1px] bg-saffron mx-auto mb-6" data-id="element-358" />
                <p className="text-base md:text-lg text-navy/80 leading-relaxed text-balance" data-id="element-359">
                  {t('pitchDeck.ecosystem.description')}
                </p>
              </div>
            </div>
          </div>;
      case 7:
        return <div className={`${slideClass} bg-navy text-warmWhite`} data-id="element-360">
            <div className="max-w-4xl mx-auto w-full text-center" data-id="element-361">
              <div className="mb-10" data-id="element-362">
                <h2 className="text-3xl md:text-5xl font-heading mb-4" data-id="element-363">
                  {t('pitchDeck.network.title')}
                </h2>
                <div className="w-12 h-[2px] bg-saffron mx-auto mb-6" data-id="element-364" />
                <p className="text-base md:text-lg text-warmWhite/70 max-w-2xl mx-auto leading-relaxed" data-id="element-365">
                  {t('pitchDeck.network.description')}
                </p>
              </div>
              <div className="flex flex-wrap justify-center gap-4" data-id="element-366">
                {tArray<string>('pitchDeck.network.locations').map((loc, idx) => <div key={idx} className="px-5 py-2.5 border border-warmWhite/20 rounded-full text-sm tracking-widest uppercase font-medium text-warmWhite/80 bg-warmWhite/5" data-id="element-367">
                      {loc}
                    </div>)}
              </div>
            </div>
          </div>;
      case 8:
        return <div className={`${slideClass} bg-ivory text-navy`} data-id="element-368">
            <div className="max-w-5xl mx-auto w-full" data-id="element-369">
              <div className="text-center mb-10" data-id="element-370">
                <h2 className="text-3xl md:text-5xl font-heading mb-4" data-id="element-371">
                  {t('pitchDeck.financial.title')}
                </h2>
                <div className="w-12 h-[2px] bg-saffron mx-auto mb-6" data-id="element-372" />
                <p className="text-base md:text-lg text-navy/80 max-w-3xl mx-auto leading-relaxed text-balance" data-id="element-373">
                  {t('pitchDeck.financial.description')}
                </p>
              </div>
              <div className="grid grid-cols-3 gap-6" data-id="element-374">
                {tArray<{
                title: string;
                desc: string;
              }>('pitchDeck.financial.tiers').map((tier, idx) => <div key={idx} className="p-6 border border-navy/10 bg-white shadow-sm rounded-sm text-center" data-id="element-375">
                    <h3 className="text-xl font-heading mb-3 text-saffron" data-id="element-376">
                      {tier.title}
                    </h3>
                    <p className="text-navy/70 leading-relaxed text-sm" data-id="element-377">
                      {tier.desc}
                    </p>
                  </div>)}
              </div>
            </div>
          </div>;
      case 9:
        return <div className={`${slideClass} bg-navy text-warmWhite text-center`} data-id="element-378">
            <div className="max-w-3xl mx-auto w-full" data-id="element-379">
              <h2 className="text-4xl md:text-6xl font-heading mb-10" data-id="element-380">
                {t('pitchDeck.cta.title')}
              </h2>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10" data-id="element-381">
                <a href="https://form.typeform.com/to/BGQF1C1p" target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto px-8 py-4 bg-saffron text-navy rounded-sm text-sm tracking-widest uppercase font-medium hover:bg-warmWhite transition-colors duration-300 shadow-lg text-center" data-id="element-382">
                  {t('pitchDeck.cta.donate')}
                </a>
                <a href="https://wa.me/6596670750" target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto px-8 py-4 bg-transparent border-2 border-warmWhite/50 text-warmWhite rounded-sm text-sm tracking-widest uppercase font-medium hover:bg-warmWhite/10 hover:border-warmWhite transition-colors duration-300 text-center" data-id="element-383">
                  {t('pitchDeck.cta.founding')}
                </a>
              </div>
              <div className="flex flex-col items-center space-y-4" data-id="element-384">
                <a href="https://form.typeform.com/to/BGQF1C1p" target="_blank" rel="noopener noreferrer" className="inline-flex items-center space-x-2 text-warmWhite/80 hover:text-warmWhite transition-colors font-medium" data-id="element-385">
                  <MailIcon className="w-5 h-5" data-id="element-386" />
                  <span data-id="element-387">{t('pitchDeck.cta.whatsapp')}</span>
                </a>
                <Link to="/" className="text-sm tracking-widest uppercase font-medium text-warmWhite/60 hover:text-warmWhite transition-colors" data-id="element-388">
                  {t('pitchDeck.backToHome')}
                </Link>
              </div>
            </div>
          </div>;
      default:
        return null;
    }
  };
  return <div className="bg-navy h-screen w-screen font-sans selection:bg-saffron/30 selection:text-navy overflow-hidden" data-id="element-389">
      {/* Floating Nav */}
      <motion.nav initial={{
      y: -100
    }} animate={{
      y: 0
    }} transition={{
      duration: 0.8,
      ease: [0.16, 1, 0.3, 1]
    }} className="fixed top-0 left-0 right-0 z-50 bg-transparent py-4" data-id="element-390">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between" data-id="element-391">
          <Link to="/" className="flex items-center space-x-2 text-warmWhite hover:text-saffron transition-colors group" data-id="element-392">
            <ArrowLeftIcon className="w-5 h-5 group-hover:-translate-x-1 transition-transform" data-id="element-393" />
            <span className="text-sm tracking-widest uppercase font-medium" data-id="element-394">
              {t('pitchDeck.backToHome')}
            </span>
          </Link>
          <div className="flex items-center space-x-4" data-id="element-395">
            <button onClick={() => setLanguage('en')} className={`text-xs font-semibold tracking-wider ${language === 'en' ? 'text-saffron' : 'text-warmWhite'}`} data-id="element-396">
              EN
            </button>
            <span className="text-warmWhite/30" data-id="element-397">|</span>
            <button onClick={() => setLanguage('pa')} className={`text-xs font-semibold tracking-wider ${language === 'pa' ? 'text-saffron' : 'text-warmWhite'}`} data-id="element-398">
              ਪੰ
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Progress Bar */}
      <div className="fixed bottom-0 left-0 right-0 h-1 bg-warmWhite/10 z-50" data-id="element-399">
        <motion.div className="h-full bg-saffron" animate={{
        width: `${progress}%`
      }} transition={{
        duration: 0.4,
        ease: 'easeOut'
      }} data-id="element-400" />
      </div>

      {/* Slide Content */}
      <div className="h-full w-full relative" data-id="element-401">
        <AnimatePresence mode="wait" custom={direction} data-id="element-402">
          <motion.div key={currentSlide} custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
          duration: 0.5,
          ease: [0.32, 0.72, 0, 1]
        }} className="absolute inset-0" data-id="element-403">
            {renderSlide(currentSlide)}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Navigation Controls */}
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center space-x-4 bg-navy/80 backdrop-blur-md px-4 py-2.5 rounded-full border border-warmWhite/10 shadow-lg" data-id="element-404">
        <button onClick={handlePrev} disabled={currentSlide === 0} className="w-8 h-8 rounded-full bg-warmWhite/10 hover:bg-warmWhite/20 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center text-warmWhite transition-colors" data-id="element-405">
          <ChevronLeftIcon className="w-4 h-4" data-id="element-406" />
        </button>

        <div className="flex items-center space-x-1.5" data-id="element-407">
          {Array.from({
          length: TOTAL_SLIDES
        }).map((_, idx) => <button key={idx} onClick={() => goToSlide(idx)} className={`rounded-full transition-all duration-300 ${idx === currentSlide ? 'w-5 h-1.5 bg-saffron' : 'w-1.5 h-1.5 bg-warmWhite/40 hover:bg-warmWhite/60'}`} data-id="element-408" />)}
        </div>

        <button onClick={handleNext} disabled={currentSlide === TOTAL_SLIDES - 1} className="w-8 h-8 rounded-full bg-warmWhite/10 hover:bg-warmWhite/20 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center text-warmWhite transition-colors" data-id="element-409">
          <ChevronRightIcon className="w-4 h-4" data-id="element-410" />
        </button>
      </div>

      {/* Slide Counter */}
      <div className="fixed bottom-6 right-6 z-50 text-warmWhite/60 text-xs tracking-widest font-medium bg-navy/70 backdrop-blur-sm px-3 py-1.5 rounded-full" data-id="element-411">
        {String(currentSlide + 1).padStart(2, '0')} / {TOTAL_SLIDES}
      </div>
    </div>;
}